import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { canAccessGoogleAdsIntegration } from "@shared/google-ads-access";
import {
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ArrowLeft,
  Tag,
  BarChart3,
  Phone,
  Zap,
  Target,
  TrendingUp,
  FileSpreadsheet,
  Search,
} from "lucide-react";

// ─── Copy Button ────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation("integrationsGuide");
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 p-1.5 rounded-md bg-white/[0.08] hover:bg-white/10 text-white/70 hover:text-white transition-colors"
      title={t("copy")}
    >
      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
    </button>
  );
}

// ─── Code Block ─────────────────────────────────────────────
function CodeBlock({ children, lang }: { children: string; lang?: string }) {
  return (
    <div className="relative group my-3">
      <CopyButton text={children.trim()} />
      <pre className="bg-[#030407] border border-white/[0.1] rounded-lg p-4 pr-12 overflow-x-auto text-sm">
        <code className="text-emerald-400 font-mono">{children.trim()}</code>
      </pre>
    </div>
  );
}

// ─── Collapsible Section ────────────────────────────────────
function Section({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/[0.08] rounded-xl overflow-hidden mb-4 bg-white/[0.03]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-white/[0.03] transition-colors"
      >
        <div className="p-2 rounded-lg bg-red-500/10 text-red-400">
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-lg font-semibold text-white flex-1">{title}</span>
        {open ? (
          <ChevronDown className="w-5 h-5 text-white/60" />
        ) : (
          <ChevronRight className="w-5 h-5 text-white/60" />
        )}
      </button>
      {open && <div className="px-5 pb-5 border-t border-white/[0.06]">{children}</div>}
    </div>
  );
}

// ─── Step Component ─────────────────────────────────────────
function Step({ num, title, children }: { num: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 my-6">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-sm font-bold border border-red-500/30">
        {num}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-medium mb-2">{title}</h4>
        <div className="text-white/70 text-sm leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  );
}

// ─── Tag Badge ──────────────────────────────────────────────
function TagBadge({ name, color }: { name: string; color: string }) {
  const colorMap: Record<string, string> = {
    green: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    red: "bg-red-500/15 text-red-400 border-red-500/30",
    yellow: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    blue: "bg-red-500/15 text-red-400 border-red-500/30",
    purple: "bg-red-500/15 text-red-400 border-red-500/30",
    gray: "bg-white/[0.06] text-white/60 border-white/[0.08]",
    orange: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${colorMap[color] || colorMap.gray}`}
    >
      <Tag className="w-3 h-3" />
      {name}
    </span>
  );
}

// ─── Funnel Diagram ─────────────────────────────────────────
function FunnelDiagram() {
  const { t } = useTranslation("integrationsGuide");
  const stages = [
    { label: t("funnel.metaAd"), tag: null, color: "blue", width: "100%" },
    { label: t("funnel.newLead"), tag: null, color: "blue", width: "85%" },
    {
      label: t("funnel.qualification"),
      tag: "kwalifikowany-lead / niekwalifikowany-lead",
      color: "yellow",
      width: "70%",
    },
    { label: t("funnel.appointmentBooked"), tag: null, color: "purple", width: "55%" },
    {
      label: t("funnel.callDone"),
      tag: "rozmowa-kwalifikowana / rozmowa-niekwalifikowana",
      color: "orange",
      width: "40%",
    },
    { label: t("funnel.saleOrLoss"), tag: "sprzedaz / przegrana", color: "green", width: "25%" },
  ];

  return (
    <div className="my-6 space-y-2">
      {stages.map((s, i) => (
        <div key={i} className="flex items-center gap-3">
          <div
            className={`h-10 rounded-lg flex items-center px-4 text-sm font-medium transition-all ${
              s.color === "blue"
                ? "bg-red-500/20 text-red-300 border border-red-500/30"
                : s.color === "yellow"
                  ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                  : s.color === "purple"
                    ? "bg-red-500/20 text-red-300 border border-red-500/30"
                    : s.color === "orange"
                      ? "bg-orange-500/20 text-orange-300 border border-orange-500/30"
                      : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
            }`}
            style={{ width: s.width }}
          >
            {s.label}
          </div>
          {s.tag && <span className="text-xs text-white/40 whitespace-nowrap">← tag: {s.tag}</span>}
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────
export default function IntegrationsGuidePage() {
  const { t } = useTranslation("integrationsGuide");
  const { user } = useAuth();
  const canSeeGoogleAds = canAccessGoogleAdsIntegration(user);
  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030407] text-white/90">
      {/* Header */}
      <div className="border-b border-white/[0.08] bg-[#030407]/95 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <a
            href="/"
            className="p-2 rounded-lg hover:bg-[#0a0c10] text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </a>
          <div>
            <h1 className="text-xl font-bold text-white">{t("header.title")}</h1>
            <p className="text-sm text-white/60">{t("header.subtitle")}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Intro Card */}
        <div className="bg-gradient-to-r from-red-500/10 to-red-500/10 border border-red-500/20 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-2">{t("intro.title")}</h2>
          <p className="text-white/70 text-sm leading-relaxed">{t("intro.description")}</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="flex items-center gap-2 text-sm text-emerald-400">
              <TrendingUp className="w-4 h-4" /> ROAS per kampania
            </div>
            <div className="flex items-center gap-2 text-sm text-amber-400">
              <Phone className="w-4 h-4" /> Show-up rate
            </div>
            <div className="flex items-center gap-2 text-sm text-red-400">
              <Target className="w-4 h-4" /> {t("intro.leadQuality")}
            </div>
            <div className="flex items-center gap-2 text-sm text-red-400">
              <BarChart3 className="w-4 h-4" /> Close rate
            </div>
          </div>
        </div>

        {/* Visual Funnel */}
        <Section title={t("sections.funnel")} icon={BarChart3} defaultOpen>
          <p className="text-white/70 text-sm mt-3 mb-2">
            {t("funnel.descPre")}
            <strong className="text-white">{t("funnel.utmMeta")}</strong>
            {t("funnel.descMid")}
            <strong className="text-white">{t("funnel.lcTags")}</strong>
            {t("funnel.descPost")}
          </p>
          <FunnelDiagram />
          <div className="mt-4 p-4 bg-[#030407]/50 rounded-lg border border-white/[0.08]">
            <p className="text-sm text-white/60">
              <strong className="text-white/80">{t("funnel.whyTagsUtmTitle")}</strong>{" "}
              {t("funnel.whyTagsUtmBody")}
            </p>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════════════ */}
        {/* META ADS — UTM Setup */}
        {/* ═══════════════════════════════════════════════════ */}
        <Section title={t("sections.metaAds")} icon={Zap} defaultOpen>
          <Step num={1} title={t("metaAds.step1.title")}>
            <p>{t("metaAds.step1.desc")}</p>
          </Step>

          <Step num={2} title={t("metaAds.step2.title")}>
            <p>{t("metaAds.step2.desc")}</p>
          </Step>

          <Step num={3} title={t("metaAds.step3.title")}>
            <CodeBlock>{`utm_source=facebook&utm_medium=paid&utm_campaign={{campaign.id}}&utm_term={{adset.id}}&utm_content={{ad.id}}`}</CodeBlock>
            <p>
              {t("metaAds.step3.descPre")}
              <code className="text-emerald-400 bg-[#0a0c10] px-1 rounded">
                {"{{campaign.id}}"}
              </code>
              {t("metaAds.step3.descMid")}
              <code className="text-emerald-400 bg-[#0a0c10] px-1 rounded">{"{{adset.id}}"}</code>
              {t("metaAds.step3.and")}
              <code className="text-emerald-400 bg-[#0a0c10] px-1 rounded">{"{{ad.id}}"}</code>
              {t("metaAds.step3.descPost")}
            </p>
          </Step>

          <Step num={4} title={t("metaAds.step4.title")}>
            <p>{t("metaAds.step4.desc")}</p>
            <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-amber-300 text-xs">
                <strong>{t("common.important")}</strong> {t("metaAds.step4.warning")}
              </p>
            </div>
          </Step>

          <div className="mt-4 p-4 bg-[#030407]/50 rounded-lg border border-white/[0.08]">
            <h4 className="text-white font-medium mb-2">{t("metaAds.table.title")}</h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.1]">
                  <th className="text-left py-2 text-white/60 font-medium">
                    {t("metaAds.table.param")}
                  </th>
                  <th className="text-left py-2 text-white/60 font-medium">
                    {t("metaAds.table.value")}
                  </th>
                  <th className="text-left py-2 text-white/60 font-medium">
                    {t("metaAds.table.stores")}
                  </th>
                </tr>
              </thead>
              <tbody className="text-white/70">
                <tr className="border-b border-white/[0.08]">
                  <td className="py-2 font-mono text-emerald-400">utm_source</td>
                  <td className="py-2">facebook</td>
                  <td className="py-2">{t("metaAds.table.sourceDesc")}</td>
                </tr>
                <tr className="border-b border-white/[0.08]">
                  <td className="py-2 font-mono text-emerald-400">utm_medium</td>
                  <td className="py-2">paid</td>
                  <td className="py-2">{t("metaAds.table.mediumDesc")}</td>
                </tr>
                <tr className="border-b border-white/[0.08]">
                  <td className="py-2 font-mono text-emerald-400">utm_campaign</td>
                  <td className="py-2">{"{{campaign.id}}"}</td>
                  <td className="py-2">{t("metaAds.table.campaignDesc")}</td>
                </tr>
                <tr className="border-b border-white/[0.08]">
                  <td className="py-2 font-mono text-emerald-400">utm_term</td>
                  <td className="py-2">{"{{adset.id}}"}</td>
                  <td className="py-2">{t("metaAds.table.adsetDesc")}</td>
                </tr>
                <tr>
                  <td className="py-2 font-mono text-emerald-400">utm_content</td>
                  <td className="py-2">{"{{ad.id}}"}</td>
                  <td className="py-2">{t("metaAds.table.adDesc")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════════════ */}
        {/* LEADCONNECTOR — Tags Setup */}
        {/* ═══════════════════════════════════════════════════ */}
        <Section title={t("sections.leadConnector")} icon={Tag} defaultOpen>
          <p className="text-white/70 text-sm mt-3 mb-4">{t("lc.intro")}</p>

          <h4 className="text-white font-medium mb-3">{t("lc.requiredTags")}</h4>
          <div className="space-y-3 mb-6">
            <TagRow
              tag="kwalifikowany-lead"
              color="green"
              when={t("lc.tags.qualifiedWhen")}
              automation={t("lc.tags.qualifiedAuto")}
            />
            <TagRow
              tag="niekwalifikowany-lead"
              color="red"
              when={t("lc.tags.unqualifiedWhen")}
              automation={t("lc.tags.unqualifiedAuto")}
            />
            <TagRow
              tag="rozmowa-nie-odbyta"
              color="gray"
              when={t("lc.tags.noShowWhen")}
              automation={t("lc.tags.noShowAuto")}
            />
            <TagRow
              tag="rozmowa-kwalifikowana"
              color="blue"
              when={t("lc.tags.qualCallWhen")}
              automation={t("lc.tags.qualCallAuto")}
            />
            <TagRow
              tag="rozmowa-niekwalifikowana"
              color="orange"
              when={t("lc.tags.unqualCallWhen")}
              automation={t("lc.tags.unqualCallAuto")}
            />
            <TagRow
              tag="sprzedaz"
              color="green"
              when={t("lc.tags.saleWhen")}
              automation={t("lc.tags.saleAuto")}
            />
            <TagRow
              tag="przegrana"
              color="red"
              when={t("lc.tags.lossWhen")}
              automation={t("lc.tags.lossAuto")}
            />
          </div>

          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
            <h4 className="text-white font-medium mb-2">{t("lc.proTip.title")}</h4>
            <p className="text-white/70 text-sm mb-3">{t("lc.proTip.desc")}</p>
            <ul className="text-sm text-white/70 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-0.5">→</span>
                <span>
                  {t("lc.proTip.trigger1Pre")}
                  <strong className="text-white">Appointment Status → No Show</strong>
                  {t("lc.proTip.trigger1Post")}
                  <TagBadge name="rozmowa-nie-odbyta" color="gray" />
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-0.5">→</span>
                <span>
                  {t("lc.proTip.trigger2Pre")}
                  <strong className="text-white">Opportunity Status → Won</strong>
                  {t("lc.proTip.trigger2Mid")}
                  <TagBadge name="sprzedaz" color="green" />
                  {t("lc.proTip.trigger2Post")}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-0.5">→</span>
                <span>
                  {t("lc.proTip.trigger3Pre")}
                  <strong className="text-white">Opportunity Status → Lost</strong>
                  {t("lc.proTip.trigger3Post")}
                  <TagBadge name="przegrana" color="red" />
                </span>
              </li>
            </ul>
          </div>

          <Step num={1} title={t("lc.step1.title")}>
            <p>{t("lc.step1.desc")}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <TagBadge name="kwalifikowany-lead" color="green" />
              <TagBadge name="niekwalifikowany-lead" color="red" />
              <TagBadge name="rozmowa-nie-odbyta" color="gray" />
              <TagBadge name="rozmowa-kwalifikowana" color="blue" />
              <TagBadge name="rozmowa-niekwalifikowana" color="orange" />
              <TagBadge name="sprzedaz" color="green" />
              <TagBadge name="przegrana" color="red" />
            </div>
            <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <p className="text-sm text-emerald-300 font-medium mb-1">
                {t("lc.step1.customFieldTitle")}
              </p>
              <p className="text-sm text-white/70">{t("lc.step1.customFieldDesc")}</p>
              <ul className="text-sm text-white/70 mt-2 space-y-1">
                <li>
                  • {t("lc.step1.fieldName")}:{" "}
                  <strong className="text-emerald-400">data_sprzedazy</strong>
                </li>
                <li>
                  • {t("lc.step1.fieldType")}: <strong className="text-white">Date</strong>
                </li>
              </ul>
              <p className="text-xs text-white/40 mt-2">{t("lc.step1.fieldNote")}</p>
            </div>
          </Step>

          <Step num={2} title={t("lc.step2.title")}>
            <p>{t("lc.step2.desc")}</p>
            <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm text-amber-300 font-medium mb-1">
                {t("lc.step2.importantTitle")}
              </p>
              <p className="text-sm text-white/70">{t("lc.step2.importantDesc")}</p>
              <ol className="text-sm text-white/70 mt-2 space-y-1 list-decimal pl-4">
                <li>
                  {t("lc.step2.action1")}
                  <TagBadge name="sprzedaz" color="green" />
                </li>
                <li>
                  {t("lc.step2.action2Pre")}
                  <strong className="text-emerald-400">data_sprzedazy</strong> ={" "}
                  <strong className="text-white">Today (current date)</strong>
                </li>
              </ol>
            </div>
            <p className="text-sm text-white/50 mt-2">{t("lc.step2.manualNote")}</p>
          </Step>

          <Step num={3} title={t("lc.step3.title")}>
            <p>{t("lc.step3.desc")}</p>
          </Step>
        </Section>

        {/* ═══════════════════════════════════════════════════ */}
        {/* GOOGLE SHEETS — Alternative to LeadConnector */}
        {/* ═══════════════════════════════════════════════════ */}
        <Section title={t("sections.googleSheets")} icon={FileSpreadsheet}>
          <div className="mt-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg mb-6">
            <p className="text-sm text-emerald-300">
              <strong>{t("common.forWho")}</strong> {t("gs.intro")}
            </p>
          </div>

          <Step num={1} title={t("gs.step1.title")}>
            <p>{t("gs.step1.desc")}</p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.1]">
                    <th className="text-left py-2 text-white/60 font-medium">
                      {t("gs.table.column")}
                    </th>
                    <th className="text-left py-2 text-white/60 font-medium">
                      {t("gs.table.required")}
                    </th>
                    <th className="text-left py-2 text-white/60 font-medium">
                      {t("gs.table.description")}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-white/70">
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">
                      {t("gs.cols.sourceColName")}
                    </td>
                    <td className="py-2">
                      <span className="text-emerald-400"></span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.sourceDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">
                      {t("gs.cols.nameColName")}
                    </td>
                    <td className="py-2">
                      <span className="text-emerald-400"></span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.nameDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">Email</td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.emailDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">Telefon</td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.phoneDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">Data</td>
                    <td className="py-2">
                      <span className="text-emerald-400"></span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.dateDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">
                      {t("gs.cols.budgetColName")}
                    </td>
                    <td className="py-2">
                      <span className="text-emerald-400"></span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.budgetDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">
                      {t("gs.cols.timelineColName")}
                    </td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.timelineDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">Cel</td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.goalDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">
                      {t("gs.cols.interestColName")}
                    </td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.interestDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">
                      Notatki handlowca 1–6
                    </td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.notesDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">campaign id</td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.campaignIdDesc")}</td>
                  </tr>
                  <tr className="border-b border-white/[0.08]">
                    <td className="py-2 font-mono text-emerald-400 text-xs">adset id</td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.adsetIdDesc")}</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-emerald-400 text-xs">ad id</td>
                    <td className="py-2">
                      <span className="text-white/40">opcja</span>
                    </td>
                    <td className="py-2 text-xs">{t("gs.cols.adIdDesc")}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-amber-300 text-xs">
                <strong>{t("common.important")}</strong> {t("gs.step1.utmNote")}
              </p>
            </div>
          </Step>

          <Step num={2} title={t("gs.step2.title")}>
            <p>{t("gs.step2.desc")}</p>
            <ol className="list-decimal list-inside text-sm text-white/70 space-y-2 mt-2">
              <li>{t("gs.step2.li1")}</li>
              <li>
                {t("gs.step2.li2Pre")}
                <strong className="text-white">{t("gs.step2.li2Bold")}</strong>
              </li>
              <li>
                {t("gs.step2.li3Pre")}
                <strong className="text-white">{t("gs.step2.li3Doc")}</strong>
                {t("gs.step2.li3Mid")}
                <strong className="text-white">{t("gs.step2.li3Format")}</strong>
              </li>
              <li>
                {t("gs.step2.li4Pre")}
                <strong className="text-white">{t("gs.step2.li4Btn")}</strong>
              </li>
            </ol>
            <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-300 text-xs">
                <strong>{t("gs.step2.altTitle")}</strong> {t("gs.step2.altDescPre")}
                <strong className="text-white">{t("gs.step2.altDescBold")}</strong>
                {t("gs.step2.altDescPost")}
              </p>
            </div>
          </Step>

          <Step num={3} title={t("gs.step3.title")}>
            <p>{t("gs.step3.desc")}</p>
            <CodeBlock>{`https://docs.google.com/spreadsheets/d/1AbCdEfGhIjKlMnOpQrStUvWxYz/edit`}</CodeBlock>
            <p className="text-xs text-white/40">{t("gs.step3.note")}</p>
          </Step>

          <Step num={4} title={t("gs.step4.title")}>
            <p>{t("gs.step4.desc")}</p>
          </Step>

          <div className="mt-4 p-4 bg-[#030407]/50 rounded-lg border border-white/[0.08]">
            <h4 className="text-white font-medium mb-2">{t("gs.analysisTitle")}</h4>
            <ul className="text-sm text-white/70 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">→</span>
                <span>
                  <strong className="text-white">{t("gs.analysis.sources")}</strong>{" "}
                  {t("gs.analysis.sourcesSub")}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">→</span>
                <span>
                  <strong className="text-white">{t("gs.analysis.budgets")}</strong>{" "}
                  {t("gs.analysis.budgetsSub")}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">→</span>
                <span>
                  <strong className="text-white">{t("gs.analysis.timeline")}</strong>{" "}
                  {t("gs.analysis.timelineSub")}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">→</span>
                <span>
                  <strong className="text-white">{t("gs.analysis.notes")}</strong>{" "}
                  {t("gs.analysis.notesSub")}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">→</span>
                <span>
                  <strong className="text-white">{t("gs.analysis.campaigns")}</strong>{" "}
                  {t("gs.analysis.campaignsSub")}
                </span>
              </li>
            </ul>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════════════ */}
        {/* RINGOSTAT — Centralka telefoniczna */}
        {/* ═══════════════════════════════════════════════════ */}
        <Section title={t("sections.ringostat")} icon={Phone}>
          <div className="mt-3 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg mb-6">
            <p className="text-sm text-cyan-300">
              <strong>{t("common.forWho")}</strong> {t("ringostat.intro")}
            </p>
          </div>

          <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-6">
            <h4 className="text-amber-300 font-medium mb-2">{t("ringostat.compareTitle")}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-emerald-400 font-semibold mb-1">
                  {t("ringostat.withRingostat")}
                </p>
                <ul className="text-xs text-white/70 space-y-1">
                  <li>• {t("ringostat.r1")}</li>
                  <li>• {t("ringostat.r2")}</li>
                  <li>• {t("ringostat.r3")}</li>
                  <li>• {t("ringostat.r4")}</li>
                  <li>• {t("ringostat.r5")}</li>
                </ul>
              </div>
              <div>
                <p className="text-xs text-amber-400 font-semibold mb-1">{t("ringostat.withLc")}</p>
                <ul className="text-xs text-white/70 space-y-1">
                  <li>• {t("ringostat.lc1")}</li>
                  <li>• {t("ringostat.lc2")}</li>
                  <li>• {t("ringostat.lc3")}</li>
                  <li>• {t("ringostat.lc4")}</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-white/40 mt-3">
              {t("ringostat.setupNote")}
              <strong className="text-white">{t("ringostat.setupNoteBold")}</strong>
            </p>
          </div>

          <Step num={1} title={t("ringostat.step1.title")}>
            <p>
              {t("ringostat.step1.goto")}{" "}
              <a
                href="https://app.ringostat.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:underline inline-flex items-center gap-1"
              >
                app.ringostat.com <ExternalLink className="h-3 w-3" />
              </a>{" "}
              {t("ringostat.step1.login")}
            </p>
          </Step>

          <Step num={2} title={t("ringostat.step2.title")}>
            <p>{t("ringostat.step2.desc")}</p>
            <p className="text-xs text-white/40 mt-2">{t("ringostat.step2.note")}</p>
          </Step>

          <Step num={3} title={t("ringostat.step3.title")}>
            <p>{t("ringostat.step3.desc")}</p>
            <ol className="list-decimal list-inside text-sm text-white/70 space-y-2 mt-2">
              <li>
                {t("ringostat.step3.li1Pre")}
                <strong className="text-white">Lead Gen+</strong>
              </li>
              <li>{t("ringostat.step3.li2")}</li>
              <li>{t("ringostat.step3.li3")}</li>
              <li>{t("ringostat.step3.li4")}</li>
            </ol>
          </Step>

          <Step num={4} title={t("ringostat.step4.title")}>
            <p>{t("ringostat.step4.desc")}</p>
            <ol className="list-decimal list-inside text-sm text-white/70 space-y-2 mt-2">
              <li>{t("ringostat.step4.li1")}</li>
              <li>{t("ringostat.step4.li2")}</li>
              <li>{t("ringostat.step4.li3")}</li>
              <li>{t("ringostat.step4.li4")}</li>
            </ol>
            <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <p className="text-emerald-300 text-xs">
                <strong>{t("ringostat.step4.noKey")}</strong> {t("ringostat.step4.noKeyDesc")}
              </p>
            </div>
          </Step>

          <Step num={5} title={t("ringostat.step5.title")}>
            <p>{t("ringostat.step5.desc")}</p>
            <p className="text-xs text-white/40 mt-2">{t("ringostat.step5.note")}</p>
          </Step>
        </Section>

        {/* VP-192: instrukcja Google Ads ukryta — integracja niegotowa
            (aplikacja nie przeszla weryfikacji Google OAuth). */}
        {canSeeGoogleAds && (
          <>
            {/* ═══════════════════════════════════════════════════ */}
            {/* GOOGLE ADS — Konfiguracja */}
            {/* ═══════════════════════════════════════════════════ */}
            <Section title={t("sections.googleAds")} icon={Search}>
              <Step num={1} title={t("googleAds.step1.title")}>
                <p>
                  {t("googleAds.step1.goto")}{" "}
                  <a
                    href="https://console.cloud.google.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline inline-flex items-center gap-1"
                  >
                    console.cloud.google.com <ExternalLink className="h-3 w-3" />
                  </a>{" "}
                  {t("googleAds.step1.desc")}
                </p>
              </Step>

              <Step num={2} title={t("googleAds.step2.title")}>
                <p>{t("googleAds.step2.desc")}</p>
                <p className="mt-2">{t("googleAds.step2.addUri")}</p>
                <CodeBlock>{`https://twoja-domena.com/api/integrations/google-ads/callback`}</CodeBlock>
              </Step>

              <Step num={3} title={t("googleAds.step3.title")}>
                <p>
                  {t("googleAds.step3.goto")}{" "}
                  <a
                    href="https://ads.google.com/aw/apicenter"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline inline-flex items-center gap-1"
                  >
                    Google Ads API Center <ExternalLink className="h-3 w-3" />
                  </a>{" "}
                  {t("googleAds.step3.desc")}
                </p>
              </Step>

              <Step num={4} title={t("googleAds.step4.title")}>
                <p>{t("googleAds.step4.desc")}</p>
                <CodeBlock>{`GOOGLE_ADS_CLIENT_ID=twoj-client-id.apps.googleusercontent.com
  GOOGLE_ADS_CLIENT_SECRET=twoj-client-secret
  GOOGLE_ADS_DEVELOPER_TOKEN=twoj-developer-token`}</CodeBlock>
              </Step>

              <Step num={5} title={t("googleAds.step5.title")}>
                <p>{t("googleAds.step5.desc")}</p>
              </Step>

              <div className="mt-4 p-3 bg-[#030407]/50 rounded-lg border border-white/[0.06]">
                <p className="text-xs font-medium text-white/80 mb-2">{t("googleAds.dataTitle")}</p>
                <ul className="space-y-1 text-xs text-white/60">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">→</span>
                    <span>
                      <strong className="text-white">{t("googleAds.data1")}</strong>{" "}
                      {t("googleAds.data1Sub")}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">→</span>
                    <span>
                      <strong className="text-white">{t("googleAds.data2")}</strong>{" "}
                      {t("googleAds.data2Sub")}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">→</span>
                    <span>
                      <strong className="text-white">{t("googleAds.data3")}</strong>{" "}
                      {t("googleAds.data3Sub")}
                    </span>
                  </li>
                </ul>
              </div>
            </Section>
          </>
        )}

        {/* ═══════════════════════════════════════════════════ */}
        {/* KPI Benchmarks */}
        {/* ═══════════════════════════════════════════════════ */}
        <Section title={t("sections.kpiBenchmarks")} icon={Target}>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.1]">
                  <th className="text-left py-3 text-white/60 font-medium">
                    {t("kpi.table.metric")}
                  </th>
                  <th className="text-left py-3 text-white/60 font-medium">
                    {t("kpi.table.benchmark")}
                  </th>
                  <th className="text-left py-3 text-white/60 font-medium">
                    {t("kpi.table.dataSource")}
                  </th>
                </tr>
              </thead>
              <tbody className="text-white/70">
                <tr className="border-b border-white/[0.08]">
                  <td className="py-3 text-white font-medium">Cost Per Lead (CPL)</td>
                  <td className="py-3">
                    <span className="text-emerald-400">≤ 25 PLN</span> {t("kpi.good")}
                    <br />
                    <span className="text-amber-400">25–40 PLN</span> {t("kpi.improve")}
                    <br />
                    <span className="text-red-400">&gt; 40 PLN</span> {t("kpi.problem")}
                  </td>
                  <td className="py-3 text-xs text-white/40">Meta Ads (spend / leads)</td>
                </tr>
                <tr className="border-b border-white/[0.08]">
                  <td className="py-3 text-white font-medium">Booking Rate</td>
                  <td className="py-3">
                    <span className="text-emerald-400">≥ 30%</span> {t("kpi.bookingDesc")}
                  </td>
                  <td className="py-3 text-xs text-white/40">
                    LeadConnector (appointments / contacts)
                  </td>
                </tr>
                <tr className="border-b border-white/[0.08]">
                  <td className="py-3 text-white font-medium">Show-up Rate</td>
                  <td className="py-3">
                    <span className="text-emerald-400">≥ 70%</span> {t("kpi.good")}
                    <br />
                    <span className="text-amber-400">50–70%</span> {t("kpi.improve")}
                    <br />
                    <span className="text-red-400">&lt; 50%</span> {t("kpi.problem")}
                  </td>
                  <td className="py-3 text-xs text-white/40">LeadConnector (showed / booked)</td>
                </tr>
                <tr className="border-b border-white/[0.08]">
                  <td className="py-3 text-white font-medium">{t("kpi.leadQuality")}</td>
                  <td className="py-3">
                    <span className="text-emerald-400">≥ 60%</span> {t("kpi.leadQualDesc")}
                  </td>
                  <td className="py-3 text-xs text-white/40">
                    LeadConnector tag: kwalifikowany-lead / niekwalifikowany-lead
                  </td>
                </tr>
                <tr className="border-b border-white/[0.08]">
                  <td className="py-3 text-white font-medium">Close Rate</td>
                  <td className="py-3">
                    <span className="text-emerald-400">≥ 20%</span> {t("kpi.good")}
                    <br />
                    <span className="text-amber-400">10–20%</span> {t("kpi.ok")}
                    <br />
                    <span className="text-red-400">&lt; 10%</span> {t("kpi.problem")}
                  </td>
                  <td className="py-3 text-xs text-white/40">
                    LeadConnector (opportunities won / showed)
                  </td>
                </tr>
                <tr>
                  <td className="py-3 text-white font-medium">ROAS</td>
                  <td className="py-3">
                    <span className="text-emerald-400">≥ 3x</span> {t("kpi.healthyBiz")}
                    <br />
                    <span className="text-amber-400">1–3x</span> {t("kpi.borderline")}
                    <br />
                    <span className="text-red-400">&lt; 1x</span> {t("kpi.losing")}
                  </td>
                  <td className="py-3 text-xs text-white/40">LeadConnector revenue / Meta spend</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════════════ */}
        {/* Checklist */}
        {/* ═══════════════════════════════════════════════════ */}
        <Section title={t("sections.checklist")} icon={Check}>
          <div className="mt-3 space-y-3">
            <CheckItem label={t("checklist.utm")} />
            <CheckItem label={t("checklist.formUtm")} />
            <CheckItem label={t("checklist.tags")} />
            <CheckItem label={t("checklist.wfNoShow")} />
            <CheckItem label={t("checklist.wfWon")} />
            <CheckItem label={t("checklist.customField")} />
            <CheckItem label={t("checklist.wfLost")} />
            <CheckItem label={t("checklist.lcConnected")} />
            <CheckItem label={t("checklist.metaConnected")} />
            <CheckItem label={t("checklist.adAccounts")} />
            <CheckItem label={t("checklist.kpi")} />
            <CheckItem label={t("checklist.gsPublished")} />
            <CheckItem label={t("checklist.gsUrl")} />
            <CheckItem label={t("checklist.gsTest")} />

            <h4 className="text-white font-medium mt-6 mb-3 flex items-center gap-2">
              <Phone className="h-4 w-4 text-cyan-400" /> {t("checklist.ringostatTitle")}
            </h4>
            <CheckItem label={t("checklist.ringostatProjectId")} />
            <CheckItem label={t("checklist.ringostatConnected")} />
            <CheckItem label={t("checklist.ringostatLc")} />
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center py-8 text-white/40 text-sm">
          <p>
            {t("footer.help")}{" "}
            <a
              href="mailto:maciej.gawronski@viral.partners"
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              maciej.gawronski@viral.partners
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Helper Components ──────────────────────────────────────
function TagRow({
  tag,
  color,
  when,
  automation,
}: {
  tag: string;
  color: string;
  when: string;
  automation: string;
}) {
  const { t } = useTranslation("integrationsGuide");
  return (
    <div className="p-3 bg-[#030407]/50 rounded-lg border border-white/[0.08] flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex-shrink-0 w-52">
        <TagBadge name={tag} color={color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white/70">{when}</p>
        <p className="text-xs text-white/40 mt-0.5">
          {t("tagWhenLabel")} {automation}
        </p>
      </div>
    </div>
  );
}

function CheckItem({ label }: { label: string }) {
  const [checked, setChecked] = useState(false);
  return (
    <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/[0.04] cursor-pointer transition-colors group">
      <input
        type="checkbox"
        checked={checked}
        onChange={() => setChecked(!checked)}
        className="w-5 h-5 rounded border-white/[0.1] bg-[#0a0c10] text-red-500 focus:ring-red-500/50 focus:ring-offset-0 cursor-pointer"
      />
      <span
        className={`text-sm transition-colors ${checked ? "text-white/40 line-through" : "text-white/70 group-hover:text-white"}`}
      >
        {label}
      </span>
    </label>
  );
}
